import { useState } from "react";
import { toast } from "react-toastify";
import { initialClientState } from "./CreateNewForm/Clients/ClientData";
import { initialVendorState } from "./CreateNewForm/Vendor/VendorData";
import { sendEntityFormDataAction } from "../../actions/createEntityFormDataActions";
import { useParentOptions } from "../../hooks/useParentOptions";
import { URL_CONFIG } from "../../config/urlConfig";
import { getUserData } from "../../utils/authnicate";
import { ROLES } from "../../constants/Roles";

export const useFormsData = () => {
  const [activeTab, setActiveTab] = useState("client");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Detect SPOC role and get their email once on mount
  const loggedInUser = getUserData();
  const userRole = loggedInUser?.roles?.[0] || "";
  const isSpoc = userRole.toUpperCase() === ROLES.SPOC.toUpperCase();
  const spocEmail = isSpoc ? loggedInUser?.email || "" : "";

  const [clientFormData, setClientFormData] = useState({
    ...initialClientState,
    // Auto-fill spocEmailAddress for SPOC role on initial load
    spocEmailAddress: spocEmail,
  });
  const [vendorFormData, setVendorFormData] = useState(initialVendorState);

  const [clientErrors, setClientErrors] = useState({});
  const [vendorErrors, setVendorErrors] = useState({});

  // ── Parent list from API — shared hook ───────────────────────────────────
  const { parentOptions: rawParentOptions } = useParentOptions();

  // Keep parentList for resolving parentName in handleSubmit
  // o.label is "CODE - Name", extract just the name part
  const parentList = rawParentOptions
    .filter((o) => o.value !== "")
    .map((o) => {
      const parts = o.label.split(" - ");
      return {
        parentCode: o.value,
        parentName: parts.length > 1 ? parts.slice(1).join(" - ") : o.label,
      };
    });

  // CreateNew uses a "None" option in addition to the shared options
  const parentOptions = [
    { value: "", label: "Select Parent Company" },
    { value: "none", label: "None" },
    ...rawParentOptions.filter((o) => o.value !== ""),
  ];

  // Single field update — functional updater avoids stale closure
  const handleClientChange = (name, value) => {
    setClientFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // When LOB type changes to non-travel, default nationality to Domestic
      if (name === "entityLOBType" && value !== "travel") {
        updated.selectedlobtypenationality = "Domestic";
      }
      return updated;
    });
    if (clientErrors[name]) {
      setClientErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }
  };
  // Batch update — used for pincode auto-fill (city + State in one render)
  const handleClientChangeMultiple = (fields) => {
    setClientFormData((prev) => ({ ...prev, ...fields }));
  };

  const handleClientBlur = () => {};

  const handleVendorChange = (name, value) => {
    setVendorFormData((prev) => ({ ...prev, [name]: value }));
    if (vendorErrors[name]) {
      setVendorErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }
  };

  const handleSubmit = async (data, type) => {
    setIsSubmitting(true);

    // KYC redirect URL — entity fills in their details via this link
    const kycURL = `${URL_CONFIG.BASE_URL}/kyc-details?`;
    const loggedInUser = getUserData();

    // Resolve parentName from the fetched parent list using selected parentCode
    const selectedParent = parentList.find(
      (p) => p.parentCode === data.parentCode,
    );

    // Payload matching
    const payload = {
      parentCode: data.parentCode === "none" ? "" : data.parentCode || "",
      parentName:
        data.parentCode === "none" ? "None" : selectedParent?.parentName || "",
      entityName: data.EntityName || data.entityName || "",
      entityCategory: data.entityCategory || "",
      entityLOBType: data.entityLOBType || "",
      aaDepartmentFunction: data.aaDepartmentFunction || "",
      emailAddress: data.contactPersonEmail || "",
      spoC_EmailAddress: data.spocEmailAddress || "",
      kyC_URL: kycURL,
      createdBy: loggedInUser?.roles[0] || loggedInUser?.email || "",
      requireKYC:
        data.selectedrequirekyc === "Yes" || data.selectedrequirekyc === true,
    };

    // console.log("Payload to Create Primary data. : ", payload);
    try {
      const result = await sendEntityFormDataAction(payload);
      if (result?.success) {
        toast.success(`Created successfully!`, {
          position: "top-right",
          autoClose: 5000,
        });
        if (type === "client") {
          setClientFormData({
            ...initialClientState,
            spocEmailAddress: spocEmail,
          });
          setClientErrors({});
        } else {
          setVendorFormData(initialVendorState);
          setVendorErrors({});
        }
      } else {
        toast.error(result?.error || "Failed to create. Please try again.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      toast.error(
        error.message || "An error occurred while submitting the form",
        { position: "top-right", autoClose: 5000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (activeTab === "client") {
      setClientFormData({ ...initialClientState, spocEmailAddress: spocEmail });
      setClientErrors({});
    } else {
      setVendorFormData(initialVendorState);
      setVendorErrors({});
    }
    toast.info("Form cleared", { position: "top-right", autoClose: 5000 });
  };

  const handleTabChange = (tab) => {
    if (!isSubmitting) setActiveTab(tab);
  };

  return {
    activeTab,
    isSubmitting,
    isSpoc,
    parentList,
    parentOptions,
    clientFormData,
    vendorFormData,
    clientErrors,
    vendorErrors,
    setClientErrors,
    setVendorErrors,
    handleClientChange,
    handleClientChangeMultiple,
    handleClientBlur,
    handleVendorChange,
    handleSubmit,
    handleCancel,
    handleTabChange,
  };
};
